// A role permission's actions, as the role editor shows and saves them. The four standard actions
// are checkbox columns; any other action (e.g. goldenstewardship's "review") is kept alongside
// them as otherActions, so saving a role never drops an action the columns do not show. The "*"
// wildcard is an other action too ("All actions"). Actions are matched, and saved, in lower case,
// as the services compare them case-insensitively.

export const STANDARD_ACTIONS = ["add", "view", "edit", "delete"];

const STANDARD_FIELDS = { add: "canAdd", view: "canView", edit: "canEdit", delete: "canDelete" };

// Display names of known actions; any other is shown with its underscores as spaces.
const ACTION_LABELS = {
    "*": "All actions",
    review: "Review",
    retire: "Retire",
    restore: "Restore",
    purge: "Purge",
    truncate: "Truncate",
    admin_break_lock: "Break lock",
};

function asList(actions) {
    if (actions === "*") {
        return ["*"];
    }
    return Array.isArray(actions) ? actions.filter((action) => typeof action === "string").map((action) => action.toLowerCase()) : [];
}

// The actions of a permission other than the four standard ones, in their order, once each.
export function otherActions(actions) {
    return [...new Set(asList(actions).filter((action) => !STANDARD_ACTIONS.includes(action)))];
}

// The four checkbox values for a permission's actions (all four for the wildcard).
export function standardActionFlags(actions) {
    const list = asList(actions);
    return Object.fromEntries(STANDARD_ACTIONS.map((action) => [STANDARD_FIELDS[action], list.includes(action) || list.includes("*")]));
}

// What a role permission row saves: its checked standard actions, then its other actions. A row
// that holds the "*" wildcard keeps it as the wildcard.
export function rowActions(row) {
    const others = otherActions(row.otherActions);
    if (others.includes("*")) {
        return "*";
    }
    return [...STANDARD_ACTIONS.filter((action) => row[STANDARD_FIELDS[action]]), ...others];
}

// The other actions to offer on a row: those its permission offers, plus those the row held when
// the role loaded and those it holds now, so an action the permission does not offer (the
// wildcard, or one granted before) can be seen, removed, and checked again.
export function offeredOtherActions(masterPermission, rowOtherActions, heldOtherActions = []) {
    return otherActions([...asList(masterPermission && masterPermission.actions), ...asList(heldOtherActions), ...asList(rowOtherActions)]);
}

// A permission that offers only its own actions, none of the standard four.
export function offersOnlyOtherActions(masterPermission) {
    const actions = asList(masterPermission && masterPermission.actions);
    return actions.length > 0 && actions.every((action) => !STANDARD_ACTIONS.includes(action));
}

export function actionLabel(action) {
    return ACTION_LABELS[action] || action.replace(/_/g, " ");
}
